/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import { ArrayUtil, Chance, LoadUtil, UIUtil } from '@suntex.core/animation';
import { LogLevel, loggedContext } from '@suntex.core/logging';
import type { GameFrame } from '@suntex.modules/core';
import { ValueText } from '@suntex.modules/core';

import {
  RULES_ADD_SUB_DATA,
  RULES_ADD_SUB_GPR,
  RULES_ADD_SUB_ROUNDS,
  RULES_ADD_SUB_START_SECONDS,
} from './data/vifRulesAddSubData';
import type { GenerateRule } from './engine/ProblemSource';
import {
  buildProblemSet,
  flag11,
  flag12Mixed,
  flag12Strict,
  flag5Round1,
  flag5Round2,
  flag5Round3,
  flag5Round4,
} from './engine/ProblemSource';
import { splitStackedEquation } from './engine/boardMath';
import {
  carryForwardUnchosen,
  pickSixRandomThenCarryUnchosen,
} from './engine/carryForward';
import { cellAt } from './engine/grid';
import { resolveRulesAddSubGameFlag } from './engine/rulesAddSubFlag';
import { RulesBoardEngine } from './engine/rulesBoardEngine';
import type { VifGameProgArgs } from './types';

const ADD = 1,
  SUB = 2;

@loggedContext({ level: LogLevel.Trace, autoLog: true })
class VIFRulesAddSubGame extends RulesBoardEngine {
  constructor(args: VifGameProgArgs) {
    super(args, 2.0);
  }

  protected get GPR(): number[] {
    return RULES_ADD_SUB_GPR;
  }

  protected get rounds(): number[] {
    return RULES_ADD_SUB_ROUNDS;
  }

  protected computeGameFlag(gameFrame: GameFrame, moduleVariant: number) {
    return resolveRulesAddSubGameFlag(
      gameFrame.moduleId,
      gameFrame.moduleVariant,
      moduleVariant,
    );
  }

  protected computeBaseGameFlag(gameFlag: number) {
    return gameFlag;
  }

  protected computeOperation(gameFlag: number) {
    return gameFlag > 5 ? SUB : ADD;
  }

  protected initGameFlag() {
    switch (this.gameFlag) {
      case 0:
        this.intLayersInGame = 2;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[0]!];
        break;
      case 1:
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[1]!];
        this.intLayersInGame = 2;
        break;
      case 2:
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[2]!];
        this.intLayersInGame = 2;
        this.scaleVar = 2;
        break;
      case 3:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[3]!];
        break;
      case 4:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[4]!];
        this.scaleVar = 2;
        break;
      case 5:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[5]!];
        this.scaleVar = 2;
        break;
      case 6:
        this.intLayersInGame = 2;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[6]!];
        break;
      case 7:
        this.intLayersInGame = 2;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[7]!];
        break;
      case 8:
        this.intLayersInGame = 2;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[8]!];
        this.scaleVar = 2;
        this.preString = 'x = ';
        break;
      case 9:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[9]!];
        break;
      case 10:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[10]!];
        break;
      case 11:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[11]!];
        break;
      case 12:
        this.intLayersInGame = 4;
        this.startSeconds = [...RULES_ADD_SUB_START_SECONDS[12]!];
        break;
      default:
        break;
    }
  }

  protected onRulesFramesSet() {
    if (this.gameFlag > 5) {
      UIUtil.setFrame(this.gameStage.rulesclip.opBtn1, 2);
    }
  }

  protected initialUiSetup() {
    UIUtil.show(this.gameStage.roundMeter);
  }

  protected startGameBody() {
    if (this.gameFlag == 5 || this.gameFlag == 11 || this.gameFlag == 12) {
      this.resetAllTiles(true);
    } else {
      this.resetAllTiles(false);
    }

    this.gameFrame.scoreboard.hideMeter();
    this.gameFrame.scoreboard.hideMusicButton();

    this.gameFrame.scoreboard.updateRound(this.gameFrame.round);

    if (this.gameFrame.gamesPerRound == 1) {
      this.gameFrame.scoreboard.updateGame(null);
    }

    if (this.intLayersInGame == 4) {
      UIUtil.setFrame(this.gmeter, 3 + this.gameFrame.game);
    } else {
      UIUtil.setFrame(this.gmeter, 8 + this.gameFrame.game);
    }
    if (RULES_ADD_SUB_ROUNDS[this.baseGameFlag] == 4) {
      UIUtil.setFrame(this.rmeter, 3 + this.gameFrame.round);
    } else {
      UIUtil.setFrame(this.rmeter, 8 + this.gameFrame.round);
    }

    UIUtil.fadeTo(this.gameStage.blocker, 0, 150);
    this.blnFirstTimeLayer = [1, 1, 1, 1];
  }

  protected onNextProblemFirstTime() {
    if (this.intLayersInGame == 4) {
      UIUtil.setFrame(this.gmeter, 3 + this.gameFrame.game);
    } else {
      UIUtil.setFrame(this.gmeter, 8 + this.gameFrame.game);
    }
    if (RULES_ADD_SUB_ROUNDS[this.baseGameFlag] == 4) {
      UIUtil.setFrame(this.rmeter, 3 + this.gameFrame.round);
    } else {
      UIUtil.setFrame(this.rmeter, 8 + this.gameFrame.round);
    }
  }

  protected populateProblem() {
    const gameFlag = this.gameFlag;
    const intLayer = this.intLayer;
    const gameFrame = this.gameFrame;
    const intColumns = this.intColumns;
    const blnFirstTimeLayer = this.blnFirstTimeLayer;
    const blnChosen = this.blnChosen;

    let topEQs: string[] = this.topEQs;
    let bottomAnswers: string[] = this.bottomAnswers;

    let arrHolder: string[] = [];
    let arrHolder2: string[] = [];
    let arr: number[] = [];
    let j;
    if (gameFlag == 0) {
      if (intLayer == 1) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag0.layer1.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag0.layer1.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        ];
      } else {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          carryForwardUnchosen(topEQs, bottomAnswers, blnChosen[0]!);
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        //blnChosen[1].splice(0);
        blnChosen[1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 1) {
      if (intLayer == 1) {
        topEQs = [...RULES_ADD_SUB_DATA.flag1.layer1.topEQs];
        bottomAnswers = [...RULES_ADD_SUB_DATA.flag1.layer1.bottomAnswers];

        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          blnFirstTimeLayer[intLayer - 1] = 0;
        }

        blnChosen[intLayer - 1] = [
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        ];
      } else {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          blnFirstTimeLayer[intLayer - 1] = 0;

          arrHolder = ArrayUtil.copy(topEQs)!;
          arrHolder2 = ArrayUtil.copy(bottomAnswers)!;

          topEQs.length = 0;
          bottomAnswers.length = 0;

          //randomly pick 1 that was used in layer 1
          let picked = Chance.randomInt(0, blnChosen[0]!.length - 1);
          while (blnChosen[0]![picked] == 0) {
            picked = Chance.randomInt(0, blnChosen[0]!.length - 1);
          }

          topEQs.push(arrHolder[picked]!);
          bottomAnswers.push(arrHolder2[picked]!);

          //then use the other 8 unused in round 1
          for (j = 0; j < blnChosen[0]!.length; j++) {
            if (blnChosen[0]![j] == 0) {
              topEQs.push(arrHolder[j]!);
              bottomAnswers.push(arrHolder2[j]!);
            }
          }

          blnChosen[1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
        } else {
          //Since not the first time time in layer 2 (mistake) we have to reshuffle.

          topEQs = [...RULES_ADD_SUB_DATA.flag1.layer1.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag1.layer1.bottomAnswers];

          blnChosen[1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        }

        //blnChosen[1].splice(0);
      }
    } else if (gameFlag == 2) {
      if (intLayer == 1) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag2.layer1.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag2.layer1.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else {
        arr = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          pickSixRandomThenCarryUnchosen(
            topEQs,
            bottomAnswers,
            blnChosen[0]!,
            arr,
          );
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        //blnChosen[1].splice(0);
        blnChosen[1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 3) {
      if (intLayer == 1) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag3.layer1.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag3.layer1.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        ];
      } else if (intLayer == 2 || intLayer == 4) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          carryForwardUnchosen(topEQs, bottomAnswers, blnChosen[intLayer - 2]!);
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1]!.length = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (intLayer == 3) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag3.layer3.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag3.layer3.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        ];
      }
    } else if (gameFlag == 4) {
      if (intLayer == 1) {
        blnChosen[intLayer - 1]!.length = 0;
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs.length = 0;
          bottomAnswers.length = 0;
          if (gameFrame.round == 1 || gameFrame.round == 3) {
            topEQs = [...RULES_ADD_SUB_DATA.flag4.layer1Round1Or3.topEQs];
            bottomAnswers = [
              ...RULES_ADD_SUB_DATA.flag4.layer1Round1Or3.bottomAnswers,
            ];
          } else if (gameFrame.round == 2) {
            topEQs = [...RULES_ADD_SUB_DATA.flag4.layer1Round2.topEQs];
            bottomAnswers = [
              ...RULES_ADD_SUB_DATA.flag4.layer1Round2.bottomAnswers,
            ];
          } else {
            topEQs = [...RULES_ADD_SUB_DATA.flag4.layer1Round4.topEQs];
            bottomAnswers = [
              ...RULES_ADD_SUB_DATA.flag4.layer1Round4.bottomAnswers,
            ];
          }
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        if (gameFrame.round < 4) {
          blnChosen[intLayer - 1] = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
        } else {
          blnChosen[intLayer - 1] = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          ];
        }
      } else {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          carryForwardUnchosen(topEQs, bottomAnswers, blnChosen[intLayer - 2]!);
          blnFirstTimeLayer[intLayer - 1] = 0;
        }

        blnChosen[intLayer - 1]!.length = 0;
        if (gameFrame.round < 4) {
          if (intLayer == 2) {
            blnChosen[intLayer - 1] = [
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            ];
          } else if (intLayer == 3) {
            blnChosen[intLayer - 1] = [
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0,
            ];
          } else {
            blnChosen[intLayer - 1] = [
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            ];
          }
        } else {
          if (intLayer == 2) {
            blnChosen[intLayer - 1] = [
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            ];
          } else if (intLayer == 3) {
            blnChosen[intLayer - 1] = [
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0,
            ];
          } else {
            blnChosen[intLayer - 1] = [
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
            ];
          }
        }
      }
    } else if (gameFlag == 5) {
      topEQs.length = 0;
      bottomAnswers.length = 0;
      blnChosen[intLayer - 1]!.length = 0;

      // The original has no else-branch for rounds outside 1-4: generation
      // is only wired for rounds 1-4, so topEQs/bottomAnswers stay empty
      // otherwise.
      const flag5RulesByRound: Record<number, GenerateRule> = {
        1: flag5Round1,
        2: flag5Round2,
        3: flag5Round3,
        4: flag5Round4,
      };
      const rule = flag5RulesByRound[gameFrame.round];
      if (rule) {
        // Legacy loop was `while (c <= intColumns * 3)` with c starting at 0
        // and incremented per accepted problem, yielding intColumns*3 + 1
        // problems (not intColumns*3). Preserved deliberately.
        const problems = buildProblemSet(
          { kind: 'generate', rule, count: intColumns * 3 + 1 },
          (min, max) => Chance.randomInt(min, max),
        );
        topEQs = problems.topEQs;
        bottomAnswers = problems.bottomAnswers;
        blnChosen[intLayer - 1] = new Array<number>(topEQs.length).fill(0);
      }
    } else if (gameFlag == 6) {
      if (intLayer == 1) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag6.layer1.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag6.layer1.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (intLayer == 2) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs.length = 0;
          bottomAnswers.length = 0;
          topEQs = [...RULES_ADD_SUB_DATA.flag6.layer2.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag6.layer2.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1]!.length = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 7) {
      if (intLayer == 1) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag7.layer1.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag7.layer1.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (intLayer == 2) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs.length = 0;
          bottomAnswers.length = 0;
          topEQs = [...RULES_ADD_SUB_DATA.flag7.layer2.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag7.layer2.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1]!.length = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 8) {
      if (intLayer == 1) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag8.layer1.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag8.layer1.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else {
        arr.length = 0;
        arr = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          pickSixRandomThenCarryUnchosen(
            topEQs,
            bottomAnswers,
            blnChosen[0]!,
            arr,
          );
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        //blnChosen[1].splice(0);
        blnChosen[1]!.length = 0;
        blnChosen[1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      }
    } else if (gameFlag == 9) {
      if (intLayer == 1) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag9.layer1.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag9.layer1.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        ];
      } else if (intLayer == 2 || intLayer == 4) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          carryForwardUnchosen(topEQs, bottomAnswers, blnChosen[intLayer - 2]!);
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1]!.length = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (intLayer == 3) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag9.layer3.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag9.layer3.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        ];
      }
    } else if (gameFlag == 10) {
      if (intLayer == 1) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag10.layer1.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag10.layer1.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        ];
      } else if (intLayer == 2 || intLayer == 4) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          carryForwardUnchosen(topEQs, bottomAnswers, blnChosen[intLayer - 2]!);
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1]!.length = 0;
        blnChosen[intLayer - 1] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      } else if (intLayer == 3) {
        if (blnFirstTimeLayer[intLayer - 1] == 1) {
          topEQs = [...RULES_ADD_SUB_DATA.flag10.layer3.topEQs];
          bottomAnswers = [...RULES_ADD_SUB_DATA.flag10.layer3.bottomAnswers];
          blnFirstTimeLayer[intLayer - 1] = 0;
        }
        blnChosen[intLayer - 1] = [
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        ];
      }
    } else if (gameFlag == 11 || gameFlag == 12) {
      topEQs.length = 0;
      bottomAnswers.length = 0;
      blnChosen[intLayer - 1]!.length = 0;

      const rule: GenerateRule =
        gameFlag == 11
          ? flag11
          : gameFrame.round <= 2
            ? flag12Strict
            : flag12Mixed;

      // Legacy loop was `while (c <= intColumns * 3)` with c starting at 0
      // and incremented per accepted problem, yielding intColumns*3 + 1
      // problems (not intColumns*3). Preserved deliberately.
      const problems = buildProblemSet(
        { kind: 'generate', rule, count: intColumns * 3 + 1 },
        (min, max) => Chance.randomInt(min, max),
      );
      topEQs = problems.topEQs;
      bottomAnswers = problems.bottomAnswers;
      blnChosen[intLayer - 1] = new Array(topEQs.length).fill(0);
    }

    this.topEQs = topEQs;
    this.bottomAnswers = bottomAnswers;
  }

  protected fillGridCell(row: number, col: number, selectedIndex: number) {
    const settingTile = cellAt(this.board, row, col);
    if (this.gameFlag == 8) {
      const [topLine, bottomLine] = splitStackedEquation(
        this.topEQs[selectedIndex]!,
      );
      ValueText.displayTwoLines(settingTile.text3, topLine, bottomLine);
    } else {
      ValueText.displayText(settingTile.text3, this.topEQs[selectedIndex], 2);
    }
  }

  protected finalizeNextProblem() {
    this.board.enabled = true;
    if (this.intLayersInGame == 4) {
      UIUtil.setFrame(this.gmeter, this.intLayer + 3);
    } else {
      UIUtil.setFrame(this.gmeter, this.intLayer + 8);
    }
    this.gameFrame.scoreboard.updateGame(this.intLayer);
    if (RULES_ADD_SUB_ROUNDS[this.baseGameFlag] == 4) {
      UIUtil.setFrame(this.rmeter, this.gameFrame.round + 3);
    } else {
      UIUtil.setFrame(this.rmeter, this.gameFrame.round + 8);
    }
  }

  protected isAltFrameGameFlag(gameFlag: number) {
    return gameFlag == 2 || gameFlag == 8;
  }

  protected getMouseCoordinatesHook(e: createjs.MouseEvent) {
    const point = UIUtil.getMousePosition(e);
    if (this.intColumns == 3) {
      return {
        row: Math.floor(point.y / 183),
        col: Math.floor(point.x / 183),
      };
    }
    return {
      row: Math.floor(point.y / 183),
      col: Math.floor(point.x / 163),
    };
  }

  protected isPointOutOfBounds(point: { x: number; y: number }) {
    return (
      point.x < 10 ||
      (this.intColumns == 3 && point.x > 570) ||
      (this.intColumns == 4 && point.x > 650) ||
      point.y < 2 ||
      point.y > 552
    );
  }

  protected computeHoverCell(point: { x: number; y: number }) {
    let tRow: number | null = null;
    let tCol: number | null = null;

    if (this.intColumns == 3) {
      if (point.x >= 15 && point.x <= 187) {
        tCol = 0;
      } else if (point.x >= 198 && point.x <= 374) {
        tCol = 1;
      } else if (point.x >= 388 && point.x <= 561) {
        tCol = 2;
      }
    } else {
      if (point.x >= 15 && point.x <= 165) {
        tCol = 0;
      } else if (point.x >= 175 && point.x <= 325) {
        tCol = 1;
      } else if (point.x >= 335 && point.x <= 485) {
        tCol = 2;
      } else if (point.x >= 495 && point.x <= 645) {
        tCol = 3;
      }
    }

    if (point.y >= -58 && point.y <= 110) {
      tRow = 0;
    } else if (point.y >= 123 && point.y <= 294) {
      tRow = 1;
    } else if (point.y >= 301 && point.y <= 475) {
      tRow = 2;
    }

    return { row: tRow, col: tCol };
  }

  protected onHoverCellChange(tRow: number | null, tCol: number | null) {
    this.fadeAllTiles(1, 0);
    this.currCol = tCol;
    this.currRow = tRow;
    if (this.currRow != null && this.currCol != null) {
      if (
        (this.currCol != this.startCol || this.currRow != this.startRow) &&
        !this.tileLocked[this.currRow]![this.currCol]
      ) {
        const changingTile = cellAt(this.board, this.currRow, this.currCol);
        UIUtil.tween(changingTile, 'alpha', 0.3, 0);
      }
    }
  }

  protected onCorrectMatch(tValue: string | number) {
    this.correctAnswer();
    this.checkForBonus(tValue);
  }

  protected playCorrectSoundHook() {
    LoadUtil.playSound('correct');
  }

  protected checkForEndBody() {
    if (this.intCorrect >= 3 * this.intColumns) {
      this.runAnimation();
      if (
        this.intLayer > this.intLayersInGame ||
        this.gameFlag == 5 ||
        this.gameFlag == 11 ||
        this.gameFlag == 12
      ) {
        LoadUtil.playSound('win');

        if (this.intLayersInGame == 4) {
          UIUtil.setFrame(this.gmeter, 3 + this.intLayer);
        } else {
          UIUtil.setFrame(this.gmeter, 8 + this.intLayer);
        }
        this.gameOver();
      }
    } else {
      this.board.enabled = true;
    }
  }

  protected runAnimationBody() {
    UIUtil.fadeTo(this.blocker, 1, 100);

    UIUtil.tween(this.blocker, 'x', 650, 150 * 2);
    UIUtil.tween(this.blocker, 'y', 500, 150 * 2);
    UIUtil.tween(this.blocker, 'width', 1, 150 * 2);
    UIUtil.tween(
      this.blocker,
      'height',
      1,
      150 * 2,
      undefined,
      this.resetAnimation.bind(this),
    );
    this.intLayer++;
  }
}

export function VIFRulesAddSub_prog(args: VifGameProgArgs) {
  new VIFRulesAddSubGame(args).run();
}
